import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { DjangoWaffleType } from "./django-waffle-type";
import { resolveVenueFlag } from "./resolve-venue-flag";

// allow window.waffle to hydrate us
declare global {
  interface Window {
    waffle: Partial<DjangoWaffleType>;
  }
}

// fallback until the real data arrives
const DEFAULT_WAFFLE: DjangoWaffleType = {
  FLAGS: {},
  SWITCHES: {},
  SAMPLES: {},
  FLAG_VENUE_IDS: {},
  FLAG_INCLUDED_VENUE_IDS: {},
  FLAG_EXCLUDED_VENUE_IDS: {},
  flag_is_active: () => false,
};

const WaffleContext = createContext<DjangoWaffleType>(DEFAULT_WAFFLE);

function readWindowWaffle(): DjangoWaffleType | null {
  if (typeof window !== "undefined" && window.waffle) {
    return {
      FLAGS: window.waffle.FLAGS ?? {},
      SWITCHES: window.waffle.SWITCHES ?? {},
      SAMPLES: window.waffle.SAMPLES ?? {},
      FLAG_VENUE_IDS: window.waffle.FLAG_VENUE_IDS ?? {},
      FLAG_INCLUDED_VENUE_IDS: window.waffle.FLAG_INCLUDED_VENUE_IDS ?? {},
      FLAG_EXCLUDED_VENUE_IDS: window.waffle.FLAG_EXCLUDED_VENUE_IDS ?? {},
      flag_is_active: window.waffle.flag_is_active ?? (() => false),
    };
  }
  return null;
}

export const WaffleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [waffle, setWaffle] = useState<DjangoWaffleType>(
    () => readWindowWaffle() ?? DEFAULT_WAFFLE
  );

  // Fallback for cases where window.waffle arrives after initial render
  useEffect(() => {
    const data = readWindowWaffle();
    if (data) {
      setWaffle(data);
    }
  }, []);

  return (
    <WaffleContext.Provider value={waffle}>{children}</WaffleContext.Provider>
  );
};

export type WaffleContextValue = DjangoWaffleType & {
  /**
   * Determine if a feature flag is active, with venue overrides taking priority:
   * 1. If venue is explicitly excluded in flag config, flag is always disabled
   * 2. If venue is explicitly included in flag config, check the base flag status
   * 3. If flag has venue scoping but current venue isn't included in flag config, disable the flag
   * 4. If flag has no venue scoping, check the base flag status
   */
  isFlagActive: (flagName: string, venueId?: number) => boolean;
};

/**
 * Checks if a feature flag is active, taking into account venue-specific scoping rules.
 *
 * Delegates to the shared resolveVenueFlag function so SSR (RedisWaffle)
 * and client-side checks use identical logic.
 */
export const checkFlagActive = (
  waffle: DjangoWaffleType,
  flagName: string,
  venueId?: number
): boolean => {
  const {
    flag_is_active,
    FLAG_VENUE_IDS,
    FLAG_INCLUDED_VENUE_IDS,
    FLAG_EXCLUDED_VENUE_IDS,
  } = waffle;

  return resolveVenueFlag(
    {
      venue_ids: FLAG_VENUE_IDS?.[flagName] ?? [],
      included_venue_ids: FLAG_INCLUDED_VENUE_IDS?.[flagName] ?? [],
      excluded_venue_ids: FLAG_EXCLUDED_VENUE_IDS?.[flagName] ?? [],
    },
    venueId,
    flag_is_active(flagName)
  );
};

/**
 * React hook to read waffle data and do venue-aware flag checks.
 * Must be used under <WaffleProvider>.
 */
export function useWaffle(): WaffleContextValue {
  const waffle = useContext(WaffleContext);
  if (!waffle) {
    throw new Error("useWaffle must be used within a WaffleProvider");
  }

  const isFlagActive = useCallback(
    (flagName: string, venueId?: number): boolean => {
      return checkFlagActive(waffle, flagName, venueId);
    },
    [waffle]
  );

  return { ...waffle, isFlagActive };
}
